import { endOfDay } from "date-fns";
import { CalendarClock, MessageSquareWarning, PackageCheck, Pencil, Undo2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { formatMaterialFecha, formatReservaRango, formatVuelve, isReservaRetrasada } from "@/lib/material";
import { MaterialFoto, MaterialUser, ReservaEstadoBadge } from "@/components/material/MaterialShared";

export default function ReservaRow({
  reserva: r,
  item,
  me,
  isAdmin,
  usersByEmail,
  showItem = false,
  highlighted = false,
  onRecoger,
  onDevolver,
  onCancelar,
  onEditar,
}) {
  const retrasada = isReservaRetrasada(r);
  const puede = r.user_email === me || isAdmin;
  const recogible = r.estado === "reservada" && new Date(r.inicio) <= endOfDay(new Date()) && new Date(r.fin) > new Date();

  return (
    <div
      className={cn(
        "flex flex-col gap-2 rounded-xl border bg-card p-3 sm:flex-row sm:items-center",
        retrasada && "border-red-300 bg-red-50/50",
        highlighted && "ring-2 ring-primary"
      )}
    >
      <div className="flex min-w-0 flex-1 items-center gap-3">
        {showItem && <MaterialFoto item={item} className="h-12 w-12 shrink-0 rounded-lg" iconClassName="h-5 w-5" />}
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            {showItem ? (
              <span className="truncate font-medium">{item?.nombre || "Equipo eliminado"}</span>
            ) : (
              <MaterialUser email={r.user_email} usersByEmail={usersByEmail} me={me} size={20} nameClassName="font-medium" />
            )}
            <ReservaEstadoBadge reserva={r} retrasada={retrasada} />
          </div>
          <p className={cn("mt-0.5 text-sm", retrasada ? "font-medium text-red-600" : "text-muted-foreground")}>
            {r.estado === "en_uso" ? `Desde ${formatMaterialFecha(r.recogido_at || r.inicio)} · ${formatVuelve(r)}` : formatReservaRango(r)}
          </p>
          {r.motivo && <p className="truncate text-xs text-muted-foreground">{r.motivo}</p>}
          {r.estado === "devuelta" && r.fin_previsto && r.devuelto_at > r.fin_previsto && (
            <p className="text-xs text-red-600">Devuelto con retraso (previsto {formatMaterialFecha(r.fin_previsto)})</p>
          )}
          {r.nota_devolucion && (
            <p className="mt-1 flex items-start gap-1 text-xs text-amber-800">
              <MessageSquareWarning className="mt-0.5 h-3.5 w-3.5 shrink-0" /> {r.nota_devolucion}
            </p>
          )}
        </div>
      </div>

      {puede && (r.estado === "reservada" || r.estado === "en_uso") && (
        <div className="flex shrink-0 flex-wrap gap-1.5">
          {r.estado === "en_uso" && (
            <>
              <Button size="sm" className="h-8 gap-1" onClick={() => onDevolver(r, item)}>
                <Undo2 className="h-3.5 w-3.5" /> Devolver
              </Button>
              <Button size="sm" variant="outline" className="h-8 gap-1" onClick={() => onEditar(r)}>
                <CalendarClock className="h-3.5 w-3.5" /> Ampliar
              </Button>
            </>
          )}
          {r.estado === "reservada" && (
            <>
              {recogible && (
                <Button size="sm" className="h-8 gap-1" onClick={() => onRecoger(r)}>
                  <PackageCheck className="h-3.5 w-3.5" /> Recoger
                </Button>
              )}
              <Button size="icon" variant="outline" className="h-8 w-8" onClick={() => onEditar(r)} aria-label="Editar reserva">
                <Pencil className="h-3.5 w-3.5" />
              </Button>
              <Button size="icon" variant="outline" className="h-8 w-8 text-destructive hover:text-destructive" onClick={() => onCancelar(r)} aria-label="Cancelar reserva">
                <X className="h-3.5 w-3.5" />
              </Button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
